import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError, setAuthToken, getAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, object = logged in
  const [club, setClub] = useState(null);
  const [clubs, setClubs] = useState([]);

  const refreshMe = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(false);
      setClub(null);
      setClubs([]);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.active_club_id) {
        try {
          const { data: c } = await api.get("/clubs/current");
          setClub(c);
        } catch {
          setClub(null);
        }
      } else {
        setClub(null);
      }
      // Fetch all clubs user belongs to
      try {
        const { data: userClubs } = await api.get("/clubs/me");
        setClubs(userClubs);
      } catch {
        setClubs([]);
      }
    } catch {
      setAuthToken(null);
      setUser(false);
      setClub(null);
      setClubs([]);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setAuthToken(data.access_token);
    setUser(data);
    await refreshMe();
    return data;
  };

  // Sign up or log in with a Google ID token (see GoogleSignInButton)
  const loginWithGoogle = async (credential) => {
    const { data } = await api.post("/auth/google", { credential });
    if (data.access_token) setAuthToken(data.access_token);
    setUser(data);
    await refreshMe();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) setAuthToken(data.access_token);
    setUser(data);
    await refreshMe();
    return data;
  };

  const switchClub = async (clubId) => {
    const { data } = await api.post(`/clubs/switch/${clubId}`);
    // Update club immediately from the response without full refresh
    let newClub = data.club;
    // Merge with cached clubs data to get member_count, active_match, subscription_badge
    const cachedClub = clubs.find(c => c.id === clubId);
    if (cachedClub) {
      newClub = { ...cachedClub, ...newClub, my_role: newClub.my_role };
    }
    setClub(newClub);
    // Refresh clubs list to update member counts, badges, etc.
    try {
      const { data: userClubs } = await api.get("/clubs/me");
      setClubs(userClubs);
    } catch {
      // ignore
    }
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setAuthToken(null);
    setUser(false);
    setClub(null);
    setClubs([]);
  };

  return (
    <AuthContext.Provider value={{ user, club, clubs, setClub, refreshMe, login, loginWithGoogle, register, logout, switchClub, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
